import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Book, CheckCircle, XCircle, RotateCcw, Volume2, 
  Award, Target, Zap, BookOpen, Edit3 
} from 'lucide-react';
import { Word, Theme, StudyModeType, StudySession } from '../types';

interface StudyModeProps {
  words: Word[];
  onUpdateWord: (word: Word) => void;
  theme: Theme;
  onClose: () => void;
}

export default function StudyMode({ words = [], onUpdateWord, theme, onClose }: StudyModeProps) {
  const [mode, setMode] = useState<StudyModeType>('flashcard');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [sessionStats, setSessionStats] = useState<StudySession>({
    id: Date.now().toString(),
    date: new Date().toISOString(),
    duration: 0,
    wordsStudied: [],
    performance: {
      correct: 0,
      incorrect: 0,
      skipped: 0
    },
    mode: 'flashcard'
  });
  const [startTime] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setSessionStats(prev => ({
        ...prev,
        duration: Math.floor((Date.now() - startTime) / 1000)
      }));
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  // Guard against empty words array
  if (words.length === 0) {
    return (
      <div className={`fixed inset-0 z-50 flex items-center justify-center ${
        theme === 'dark' ? 'bg-gray-900' : 'bg-white'
      }`}>
        <div className="text-center p-8">
          <h2 className={`text-2xl font-bold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            No Words Available
          </h2>
          <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
            Add some words to your collection to start studying!
          </p>
          <button
            onClick={onClose}
            className="mt-6 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const currentWord = words[currentIndex];

  const calculateNextReviewDate = (word: Word, wasCorrect: boolean) => {
    const now = new Date();
    const reviewCount = word.reviewCount || 0;
    let daysUntilNextReview = 1;

    if (wasCorrect) {
      // Spaced repetition intervals: 1, 3, 7, 14, 30 days
      daysUntilNextReview = Math.min(30, Math.pow(2, reviewCount));
    } else {
      // Reset to 1 day if incorrect
      daysUntilNextReview = 1;
    }

    now.setDate(now.getDate() + daysUntilNextReview);
    return now.toISOString();
  };

  const handleAnswer = (correct: boolean) => {
    const updatedWord = {
      ...currentWord,
      mastery: Math.max(0, Math.min(100, currentWord.mastery + (correct ? 10 : -5))),
      lastReviewed: new Date().toISOString(),
      nextReview: calculateNextReviewDate(currentWord, correct),
      reviewCount: (currentWord.reviewCount || 0) + 1,
      streak: correct ? (currentWord.streak || 0) + 1 : 0
    };

    onUpdateWord(updatedWord);
    setSessionStats(prev => ({
      ...prev,
      wordsStudied: [...prev.wordsStudied, currentWord.word],
      performance: {
        ...prev.performance,
        correct: prev.performance.correct + (correct ? 1 : 0),
        incorrect: prev.performance.incorrect + (correct ? 0 : 1)
      }
    }));

    if (currentIndex < words.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setShowAnswer(false);
      setUserInput('');
    } else {
      onClose();
    }
  };

  const playPronunciation = () => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(currentWord.word);
      window.speechSynthesis.speak(utterance);
    }
  };

  return (
    <div className={`fixed inset-0 z-50 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value as StudyModeType)}
              className={`px-3 py-2 rounded-lg ${
                theme === 'dark'
                  ? 'bg-gray-800 text-white border-gray-700'
                  : 'bg-white text-gray-900 border-gray-200'
              }`}
            >
              <option value="flashcard">Flashcards</option>
              <option value="writing">Writing Practice</option>
            </select>
            
            <div className={`text-lg font-semibold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              {currentIndex + 1}/{words.length}
            </div>
          </div>
          
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg ${
              theme === 'dark'
                ? 'bg-gray-800 text-white hover:bg-gray-700'
                : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
            }`}
          >
            End Session
          </button>
        </div>

        <div className={`p-8 rounded-lg shadow-lg mb-8 ${
          theme === 'dark' ? 'bg-gray-800' : 'bg-white'
        }`}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h2 className={`text-3xl font-bold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              {mode === 'writing' ? 'Write the word:' : currentWord.word}
            </h2>
          </motion.div>

          <div className="flex justify-center gap-4 my-6">
            <button
              onClick={playPronunciation}
              className={`p-3 rounded-full ${
                theme === 'dark'
                  ? 'bg-gray-700 text-purple-400 hover:bg-gray-600'
                  : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
              }`}
            >
              <Volume2 size={24} />
            </button>
          </div>

          {mode === 'writing' && (
            <div className="max-w-md mx-auto mb-6">
              <input
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                className={`w-full px-4 py-3 text-lg rounded-lg ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-white border-gray-600'
                    : 'bg-white text-gray-900 border-gray-200'
                }`}
                placeholder="Type your answer..."
              />
            </div>
          )}

          {showAnswer && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-4"
            >
              <p className={`text-lg ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {currentWord.definition}
              </p>
              
              {currentWord.examples?.map((example, index) => (
                <p key={index} className={`text-sm italic ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  "{example}"
                </p>
              ))}
            </motion.div>
          )}

          <div className="flex justify-center gap-4 mt-8">
            {!showAnswer ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAnswer(true)}
                className={`px-6 py-3 rounded-lg ${
                  theme === 'dark'
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                Show Answer
              </motion.button>
            ) : (
              <>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleAnswer(false)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg ${
                    theme === 'dark'
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  <XCircle size={20} />
                  Incorrect
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleAnswer(true)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-lg ${
                    theme === 'dark'
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  <CheckCircle size={20} />
                  Correct
                </motion.button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <div className={`p-4 rounded-lg ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className={`text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Correct
            </div>
            <div className={`text-2xl font-bold ${
              theme === 'dark' ? 'text-green-400' : 'text-green-600'
            }`}>
              {sessionStats.performance.correct}
            </div>
          </div>
          
          <div className={`p-4 rounded-lg ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className={`text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Incorrect
            </div>
            <div className={`text-2xl font-bold ${
              theme === 'dark' ? 'text-red-400' : 'text-red-600'
            }`}>
              {sessionStats.performance.incorrect}
            </div>
          </div>
          
          <div className={`p-4 rounded-lg ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className={`text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Progress
            </div>
            <div className={`text-2xl font-bold ${
              theme === 'dark' ? 'text-purple-400' : 'text-purple-600'
            }`}>
              {Math.round((currentIndex / words.length) * 100)}%
            </div>
          </div>
          
          <div className={`p-4 rounded-lg ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className={`text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Time
            </div>
            <div className={`text-2xl font-bold ${
              theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
            }`}>
              {Math.floor(sessionStats.duration / 60)}:{(sessionStats.duration % 60).toString().padStart(2, '0')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}